import React, { Suspense, useState } from "react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";
import { motion } from 'framer-motion';
import {
  Layout,
  Code,
  Paintbrush,
  Settings,
  Globe,
  Laptop,
  Shield,
  RefreshCw,
  ArrowRight,
  X,
  Minus,
  Square,
  ExternalLink,
} from 'lucide-react';

// Préchargement du modèle
useGLTF.preload("/models/wapuu.glb");

function Model() {
  const { scene } = useGLTF("/models/wapuu.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

function CarScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      className="fixed inset-0">
      <color attach="background" args={["#00762F"]} />
      <Suspense fallback={null}>
        <Stage environment="city">
          <Model />
        </Stage>
        <Environment preset="city" />
        <OrbitControls autoRotate />
      </Suspense>
    </Canvas>
  );
}

// Composant BrowserWindow pour créer des fenêtres de visualisation
const BrowserWindow = ({ title, url, isOpen, onClose }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
    >
      {/* Overlay sombre */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      <div className={`relative ${isMaximized ? 'w-full h-full' : 'w-11/12 max-w-5xl'} ${isMinimized ? 'h-auto' : ''}`}>
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Barre de titre */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4 text-white" />
              <span className="text-white font-medium text-sm">{title}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setIsMinimized(!isMinimized)}
                className="w-3 h-3 bg-yellow-400 rounded-full hover:bg-yellow-500 transition-colors flex items-center justify-center"
              >
                <Minus className="w-2 h-2 text-yellow-800" />
              </button>
              <button 
                onClick={() => setIsMaximized(!isMaximized)}
                className="w-3 h-3 bg-green-400 rounded-full hover:bg-green-500 transition-colors flex items-center justify-center"
              >
                <Square className="w-2 h-2 text-green-800" />
              </button>
              <button 
                onClick={onClose}
                className="w-3 h-3 bg-red-400 rounded-full hover:bg-red-500 transition-colors flex items-center justify-center"
              >
                <X className="w-2 h-2 text-red-800" />
              </button>
            </div>
          </div>
          
          {/* Barre d'adresse */}
          {!isMinimized && (
            <div className="bg-gray-100 px-4 py-2 flex items-center space-x-2 border-b border-gray-200">
              <div className="flex-1 bg-white rounded-md px-3 py-1 text-sm text-gray-600 flex items-center justify-between">
                <span>{url}</span>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                </a>
              </div>
            </div>
          )}
          
          {/* Contenu - iframe */}
          {!isMinimized && (
            <div className="bg-gray-50" style={{ height: isMaximized ? 'calc(100vh - 120px)' : '600px' }}>
              <iframe 
                src={url} 
                className="w-full h-full"
                title={title}
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const WordPress = () => {
  const [openWindows, setOpenWindows] = useState({});

  const toggleWindow = (key) => {
    setOpenWindows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const features = [
    {
      icon: <Layout className="w-6 h-6" />,
      title: 'Design Personnalisable',
      description: 'Créez un site unique qui reflète votre identité avec nos thèmes personnalisables.'
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: 'Extensions Puissantes',
      description: 'Étendez les fonctionnalités de votre site avec notre large choix d\'extensions.'
    },
    {
      icon: <Paintbrush className="w-6 h-6" />,
      title: 'Interface Intuitive',
      description: 'Une interface simple et conviviale pour gérer votre contenu facilement.'
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: 'Configuration Facile',
      description: 'Installez et configurez votre site WordPress en quelques clics.'
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'SEO Optimisé',
      description: 'Améliorez votre visibilité sur les moteurs de recherche.'
    },
    {
      icon: <Laptop className="w-6 h-6" />,
      title: 'Responsive Design',
      description: 'Votre site s\'adapte parfaitement à tous les appareils.'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Sécurité Renforcée',
      description: 'Protégez votre site avec nos solutions de sécurité avancées.'
    },
    {
      icon: <RefreshCw className="w-6 h-6" />,
      title: 'Mises à jour Régulières',
      description: 'Bénéficiez des dernières fonctionnalités et correctifs de sécurité.'
    }
  ];

  const wordpressLinks = [
    {
      key: 'wp-24-26',
      title: 'WordPress 2024-2026',
      url: 'https://web.cfa-montargis.com/wordpress-24-26',
      icon: <Globe className="w-5 h-5 text-green-400 mr-3" />,
      promotion: 'Promotion 2024-2026'
    },
    {
      key: 'wp-admin-24-26',
      title: 'Admin WordPress 2024-2026',
      url: 'https://web.cfa-montargis.com/wordpress-24-26/wp-admin',
      icon: <Settings className="w-5 h-5 text-green-400 mr-3" />,
      promotion: 'Promotion 2024-2026'
    },
    {
      key: 'wp-25-27',
      title: 'WordPress 2025-2027',
      url: 'https://web.cfa-montargis.com/wordpress-25-27',
      icon: <Globe className="w-5 h-5 text-green-400 mr-3" />,
      promotion: 'Promotion 2025-2027'
    },
    {
      key: 'wp-admin-25-27',
      title: 'Admin WordPress 2025-2027',
      url: 'https://web.cfa-montargis.com/wordpress-25-27/wp-admin',
      icon: <Settings className="w-5 h-5 text-green-400 mr-3" />,
      promotion: 'Promotion 2025-2027'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="min-h-screen pt-48">
      <div className="fixed inset-0 z-0">
        <CarScene />
      </div>

      <Loader />

      {/* Fenêtres de visualisation */}
      {wordpressLinks.map(link => (
        <BrowserWindow
          key={link.key}
          title={link.title}
          url={link.url}
          isOpen={openWindows[link.key]}
          onClose={() => toggleWindow(link.key)}
        />
      ))}

      <div className="container mx-auto px-4 relative z-10">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500">
            Créez votre site avec WordPress
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto bg-slate-800/60 p-4 rounded-lg backdrop-blur-sm">
            La plateforme de gestion de contenu la plus populaire pour créer des sites web professionnels
          </p>
        </motion.div>

        {/* Features Grid avec animations d'icônes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="bg-slate-800/60 backdrop-blur-sm rounded-lg p-6 hover:bg-slate-700/60 transition-all duration-300"
            >
              <motion.div 
                className="text-green-400 mb-4"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                {feature.icon}
              </motion.div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-300">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* WordPress Links Section remplacé par le CTA */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-16 mb-32"
        >
          <div className="bg-gradient-to-r from-blue-600 to-green-400 p-8 rounded-lg max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500">
              Accéder aux plateformes WordPress
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-slate-800/70 backdrop-blur-sm rounded-lg p-6 border border-slate-700"
              >
                <h3 className="text-xl font-semibold mb-4 text-yellow-400">
                  Promotion 2024-2026
                </h3>
                <div className="space-y-3">
                  {wordpressLinks
                    .filter(link => link.promotion === 'Promotion 2024-2026')
                    .map(link => (
                      <div key={link.key} className="group">
                        <div className="flex items-center bg-slate-700/50 hover:bg-slate-700/80 p-3 rounded-lg transition-all duration-200">
                          <motion.div
                            whileHover={{ scale: 1.2, rotate: 360 }}
                            transition={{ duration: 0.3 }}
                          >
                            {link.icon}
                          </motion.div>
                          <span className="text-white flex-1">{link.title}</span>
                          <button
                            onClick={() => toggleWindow(link.key)}
                            className="text-blue-400 hover:text-blue-300 px-3 py-1 text-sm rounded bg-slate-600/50 hover:bg-slate-600 mr-2 transition-all"
                          >
                            Aperçu
                          </button>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ArrowRight className="w-4 h-4 text-white hover:text-green-400 transition-colors" />
                          </a>
                        </div>
                      </div>
                    ))}
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-slate-800/70 backdrop-blur-sm rounded-lg p-6 border border-slate-700"
              >
                <h3 className="text-xl font-semibold mb-4 text-yellow-400">
                  Promotion 2025-2027
                </h3>
                <div className="space-y-3">
                  {wordpressLinks
                    .filter(link => link.promotion === 'Promotion 2025-2027')
                    .map(link => (
                      <div key={link.key} className="group">
                        <div className="flex items-center bg-slate-700/50 hover:bg-slate-700/80 p-3 rounded-lg transition-all duration-200">
                          <motion.div
                            whileHover={{ scale: 1.2, rotate: 360 }}
                            transition={{ duration: 0.3 }}
                          >
                            {link.icon}
                          </motion.div>
                          <span className="text-white flex-1">{link.title}</span>
                          <button
                            onClick={() => toggleWindow(link.key)}
                            className="text-blue-400 hover:text-blue-300 px-3 py-1 text-sm rounded bg-slate-600/50 hover:bg-slate-600 mr-2 transition-all"
                          >
                            Aperçu
                          </button>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ArrowRight className="w-4 h-4 text-white hover:text-green-400 transition-colors" />
                          </a>
                        </div>
                      </div>
                    ))}
                </div>
              </motion.div>
            </div>
            
            <p className="text-lg mt-8 text-white/90">
              Découvrez la puissance de WordPress avec nos formateurs
            </p>
            
            <motion.a
              href="https://web.cfa-montargis.com/wordpress/wp-login.php?redirect_to=https%3A%2F%2Fweb.cfa-montargis.com%2Fwordpress%2Fwp-admin%2F&reauth=1"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block mt-6 bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
            >
              Commencer maintenant
            </motion.a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default WordPress;